import { useHost } from '@xingternal/crate-xinglet';
import type { Metadata } from '@xingternal/crate-xinglet';

export const useRoutingData = (): RouteMeta[] => {
  const host = useHost();
  const metadata = host.getXingletMetadata<Contributes>();
  const data = createDataLayer(metadata);
  return data;
};

export const createDataLayer = (
  metadata: Metadata<Contributes>[]
): RouteMeta[] => {
  return metadata
    .map((item) => ({
      xinglet: item.name,
      ...item.contributes,
    }))
    .filter((item) => 'route' in item || 'routes' in item)
    .map((item) => {
      const { routes, route, ...rest } = item;

      if (routes) {
        return routes.map((route: string) => ({
          route,
          ...rest,
        }));
      } else {
        return [{ route, ...rest }];
      }
    })
    .flat()
    .map(
      ({
        route,
        xinglet,
        layout,
        activeItem,
        visibleTo,
        trackingChannel,
        pageName,
        disableCookieConsentBanner,
        cacheMaxAge,
      }) => ({
        route: route || '',
        xinglet,
        layout: layout || 'xing',
        activeItem,
        visibleTo: {
          loggedIn: visibleTo?.loggedIn ?? true,
          loggedOut: visibleTo?.loggedOut ?? false,
          softLoggedIn: visibleTo?.softLoggedIn ?? false,
        },
        trackingChannel,
        pageName,
        disableCookieConsentBanner: disableCookieConsentBanner ?? false,
        cacheMaxAge,
      })
    );
};

export type RouteMeta = {
  route: string;
  xinglet: string;
  layout: Layout;
  activeItem?: string;
  visibleTo: {
    loggedIn: boolean;
    loggedOut: boolean;
    softLoggedIn: boolean;
  };
  trackingChannel?: string;
  pageName?: string;
  disableCookieConsentBanner: boolean;
  cacheMaxAge?: number;
};

export type Layout =
  | 'xing'
  | 'zen'
  | 'xing-gridless'
  | 'zen-gridless'
  | 'blank';

export type Contributes = {
  route?: string;
  routes?: string[];
  layout?: Layout;
  trackingChannel?: string;
  pageName?: string;
  visibleTo?: {
    loggedIn?: boolean;
    loggedOut?: boolean;
    softLoggedIn?: boolean;
  };
  activeItem?: string;
  disableCookieConsentBanner?: boolean;
  cacheMaxAge?: number;
};
